import React, { useState } from "react";
import {
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";

import {
  useAppKit,
  useAppKitAccount,
  useDisconnect,
} from "@reown/appkit/react";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const location = useLocation();

  /* =========================================================
     REOWN APPKIT
  ========================================================= */

  const { open } = useAppKit();

  const {
    address,
    isConnected,
  } = useAppKitAccount();

  const { disconnect } = useDisconnect();

  /* =========================================================
     NAVIGATION
  ========================================================= */

  const navItems = [
    {
      name: "Dashboard",
      href: "/dashboard",
    },
    {
      name: "Leaderboard",
      href: "/leaderboard",
    },
    {
      name: "League",
      href: "/league",
    },
    {
      name: "Rewards",
      href: "/rewards",
    },
    {
      name: "Profile",
      href: "/profile",
    },
    {
      name: "History",
      href: "/history",
    },
  ];

  /* =========================================================
     HELPERS
  ========================================================= */

  const shortAddress = (walletAddress) => {
    if (!walletAddress) {
      return "";
    }

    return `${walletAddress.slice(
      0,
      6
    )}...${walletAddress.slice(-4)}`;
  };

  /* =========================================================
     CONNECT WALLET
  ========================================================= */

  const handleWallet = async () => {
    try {
      await open({
        view: "Connect",
      });
    } catch (error) {
      console.error(
        "Wallet connection error:",
        error
      );
    }
  };

  /* =========================================================
     OPEN ACCOUNT
  ========================================================= */

  const handleAccount = async () => {
    try {
      await open({
        view: "Account",
      });
    } catch (error) {
      console.error(
        "Account modal error:",
        error
      );
    }
  };

  /* =========================================================
     DISCONNECT
  ========================================================= */

  const handleDisconnect = async () => {
    try {
      await disconnect({
        namespace: "eip155",
      });

      try {
        localStorage.removeItem(
          "walletAddress"
        );

        localStorage.removeItem(
          "walletConnected"
        );
      } catch (storageError) {
        console.error(
          "Local storage cleanup error:",
          storageError
        );
      }

      window.dispatchEvent(
        new CustomEvent("walletDisconnected")
      );
    } catch (error) {
      console.error(
        "Wallet disconnect error:",
        error
      );
    }
  };

  /* =========================================================
     MOBILE MENU
  ========================================================= */

  const closeMobileMenu = () => {
    setMenuOpen(false);
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/[0.08] bg-black">

      {/* =====================================================
          DESKTOP / MAIN BAR
      ===================================================== */}

      <div className="mx-auto flex h-[68px] w-full max-w-[1400px] items-center justify-between px-4 sm:h-[72px] sm:px-6 lg:h-[76px] lg:px-8">

        {/* ===================================================
            LOGO
        =================================================== */}

        <Link
          to="/dashboard"
          className="flex min-w-0 shrink-0 items-center"
        >
          <img
            src="/kindlogo.png"
            alt="KIND Trading League"
            className="h-9 w-auto object-contain sm:h-10 md:h-11 lg:h-12"
          />
        </Link>

        {/* ===================================================
            DESKTOP NAVIGATION
        =================================================== */}

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                [
                  "relative rounded-[8px] px-3 py-2.5 xl:px-4 xl:py-3",
                  "text-[10px] font-semibold uppercase",
                  "tracking-[0.14em] transition duration-200",
                  isActive
                    ? "bg-[#ffcc66]/10 text-[#ffcc66]"
                    : "text-[#858585] hover:bg-white/[0.04] hover:text-white",
                ].join(" ")
              }
            >
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* ===================================================
            DESKTOP WALLET
        =================================================== */}

        <div className="hidden items-center gap-2 sm:gap-3 lg:flex">

          {isConnected && address ? (
            <>
              {/* Connected Address */}

              <button
                type="button"
                onClick={handleAccount}
                className="rounded-[8px] border border-[#ffcc66]/20 bg-[#ffcc66]/[0.06] px-3 py-2.5 transition hover:border-[#ffcc66]/40 hover:bg-[#ffcc66]/[0.10] xl:px-4 xl:py-3"
              >
                <span className="font-mono text-[10px] font-semibold text-[#ffcc66] xl:text-[11px]">
                  {shortAddress(address)}
                </span>
              </button>

              {/* Disconnect */}

              <button
                type="button"
                onClick={handleDisconnect}
                className="rounded-[8px] border border-white/10 bg-transparent px-3 py-2.5 text-[9px] font-semibold uppercase tracking-[0.13em] text-[#8d8d8d] transition hover:border-[#ffcc66]/30 hover:text-[#ffcc66] xl:px-4 xl:py-3 xl:text-[10px]"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleWallet}
              className="rounded-[8px] border border-[#ffcc66] bg-[#ffcc66] px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.13em] text-black shadow-[0_0_24px_rgba(255,204,102,0.12)] transition hover:brightness-110 xl:px-5 xl:py-3 xl:text-[11px]"
            >
              Connect Wallet
            </button>
          )}
        </div>

        {/* ===================================================
            MOBILE MENU BUTTON
        =================================================== */}

        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] border border-[#ffcc66]/20 bg-black text-[#ffcc66] transition hover:bg-[#ffcc66]/10 lg:hidden"
          aria-label="Open navigation"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      <div
        className={`fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
          menuOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
        onClick={closeMobileMenu}
      >

        {/* ===================================================
            MOBILE DRAWER
        =================================================== */}

        <div
          className={`absolute right-0 top-0 flex h-full w-[88%] max-w-[360px] transform flex-col border-l border-white/10 bg-black transition-transform duration-300 sm:w-[360px] ${
            menuOpen
              ? "translate-x-0"
              : "translate-x-full"
          }`}
          onClick={(event) =>
            event.stopPropagation()
          }
        >

          {/* ===============================================
              DRAWER HEADER
          =============================================== */}

          <div className="flex min-h-[72px] items-center justify-between border-b border-white/10 px-4 sm:px-5">

            <Link
              to="/dashboard"
              onClick={closeMobileMenu}
              className="flex min-w-0 items-center"
            >
              <img
                src="/kindlogo.png"
                alt="KIND Trading League"
                className="h-10 w-auto object-contain sm:h-11"
              />
            </Link>

            <button
              type="button"
              onClick={closeMobileMenu}
              className="ml-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-white/10 bg-black text-xl text-white transition hover:border-[#ffcc66]/30 hover:text-[#ffcc66]"
              aria-label="Close navigation"
            >
              ×
            </button>
          </div>

          {/* ===============================================
              MOBILE WALLET
          =============================================== */}

          <div className="border-b border-white/10 p-4 sm:p-5">

            {isConnected && address ? (
              <div className="rounded-[12px] border border-[#ffcc66]/15 bg-[#ffcc66]/[0.05] p-4">

                <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#777]">
                  Connected Wallet
                </p>

                <button
                  type="button"
                  onClick={handleAccount}
                  className="mt-2 block w-full text-left"
                >
                  <p className="break-all font-mono text-sm font-semibold text-[#ffcc66]">
                    {shortAddress(address)}
                  </p>

                  <p className="mt-2 break-all font-mono text-[9px] text-[#555]">
                    {address}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    await handleDisconnect();
                    closeMobileMenu();
                  }}
                  className="mt-4 w-full rounded-[9px] border border-white/10 bg-black px-4 py-3 text-[10px] font-bold uppercase tracking-[0.13em] text-[#999] transition hover:border-[#ffcc66]/30 hover:text-[#ffcc66]"
                >
                  Disconnect Wallet
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleWallet}
                className="w-full rounded-[10px] bg-[#ffcc66] px-5 py-4 text-[11px] font-black uppercase tracking-[0.13em] text-black shadow-[0_0_25px_rgba(255,204,102,0.08)] transition hover:brightness-110"
              >
                Connect Wallet
              </button>
            )}
          </div>

          {/* ===============================================
              MOBILE NAVIGATION
          =============================================== */}

          <nav className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-4 py-5 sm:gap-2 sm:px-5">

            {navItems.map((item) => {
              const isActive =
                location.pathname === item.href;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={closeMobileMenu}
                  className={`rounded-[10px] border px-4 py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition sm:py-4 ${
                    isActive
                      ? "border-[#ffcc66]/20 bg-[#ffcc66]/10 text-[#ffcc66]"
                      : "border-transparent text-[#8d8d8d] hover:bg-white/[0.04] hover:text-white"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* ===============================================
              MOBILE FOOTER
          =============================================== */}

          <div className="border-t border-white/10 px-4 py-4 sm:px-5">
            <p className="text-center text-[8px] uppercase tracking-[0.18em] text-[#444]">
              KIND Trading League
            </p>
          </div>

        </div>
      </div>
    </header>
  );
};

export default Navbar;